import dynamic from 'next/dynamic';
import { createContext, useContext } from 'react';

import type { TokenPriceChartProps } from './TokenPriceChart';
import { TokenPriceChartLoading } from './TokenPriceChartLoading';

export type { TokenPriceChartProps };

type TokenPriceChartContextValue = {
  height: number;
  showBottomControls: boolean;
  hasTopSelector: boolean;
};

// The dynamic loading component does not receive chart props, so context provides the layout values needed to reserve space.
const TokenPriceChartContext = createContext<TokenPriceChartContextValue>({
  height: 0,
  showBottomControls: false,
  hasTopSelector: false,
});

const DynamicTokenPriceChartLoading = () => {
  const loadingProps = useContext(TokenPriceChartContext);
  return <TokenPriceChartLoading {...loadingProps} />;
};

// Keep d3 out of the importing surface until a token-price chart is rendered.
const DynamicTokenPriceChart = dynamic<TokenPriceChartProps>(
  () => import('./TokenPriceChart').then((mod) => mod.TokenPriceChart),
  { ssr: false, loading: DynamicTokenPriceChartLoading },
);

export const TokenPriceChart = ({
  height,
  alternateToken,
  showTimeframes = false,
  enableCandlestickView = false,
  ...chartProps
}: TokenPriceChartProps) => (
  <TokenPriceChartContext.Provider
    value={{
      height,
      showBottomControls: showTimeframes || enableCandlestickView,
      hasTopSelector: chartProps.token != null && alternateToken != null,
    }}
  >
    <DynamicTokenPriceChart
      {...chartProps}
      alternateToken={alternateToken}
      height={height}
      showTimeframes={showTimeframes}
      enableCandlestickView={enableCandlestickView}
    />
  </TokenPriceChartContext.Provider>
);
